import React, { useState, useCallback, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { useRadarData } from '../../../../../hooks/useRadarData';
import useChartData from '../../../../TrendRadarChart/hooks/useChartData';
import useSearch from '../../../../TrendRadarChart/hooks/useSearch';
import useTooltip from '../../../../TrendRadarChart/hooks/useTooltip';
import useZoomPan from '../../../../TrendRadarChart/hooks/useZoomPan';
import { TREE_CHART_MODES, SEGMENT_TYPE, APPROVAL_STAGES } from '../../../../../constants/radar';
import TreeChart from '../../../../TrendRadarChart/components/TreeChart';
import ZoomControls from '../../../../TrendRadarChart/components/ZoomControls';
import { FormContainer, FormSection, SectionTitle } from '../../styles';
import LoadingSpinner from '../../../../common/LoadingSpinner';
import EmptyState from '../../../../common/EmptyState';
import { ChartPanel } from './styles';
import { HeaderButton } from '../../../../common/styles';

const ProjectRadar = ({ projectData, readOnly = false }) => {
  const navigate = useNavigate();
  
  // State hooks
  const [projectHierarchyData, setProjectHierarchyData] = useState(null);
  const [expandedPath, setExpandedPath] = useState([]);
  const [isTreeExpanded, setIsTreeExpanded] = useState(true);
  const [initialCheckedLeafs, setInitialCheckedLeafs] = useState(new Set());

  // Data fetching hook
  const { data: radarData, loading: radarLoading, error: radarError } = useRadarData({
    skip: !projectData?.leafSegmentProjects?.length
  });

  // Check if leaf is assigned to the project (including custom segments)
  const isLeafAssigned = useCallback((leafId) => {
    if (!projectData?.leafSegmentProjects) {
      return false;
    }
    return projectData.leafSegmentProjects.some(
      (leafSegment) => leafSegment.leafSegment?.segmentId === leafId || leafSegment.customSegment?.id === leafId
    );
  }, [projectData]);

  // Get filtered data
  const getFilteredData = useCallback(() => {
    let filteredData = projectHierarchyData;
    if (!filteredData) return null;

    // Apply selection filter
    const filterBySelection = (node) => {
      if (!node) return null;

      if (!node.subcategories || node.subcategories.length === 0) {
        return initialCheckedLeafs.has(node.leafSegmentId) ? node : null;
      }

      const filteredSubcategories = (node.subcategories || [])
        .map(filterBySelection)
        .filter(Boolean);

      return filteredSubcategories.length > 0
        ? { ...node, subcategories: filteredSubcategories }
        : null;
    };

    const selectionFiltered = filterBySelection(filteredData);
    return selectionFiltered || {
      ...filteredData,
      subcategories: []
    };
  }, [projectHierarchyData, initialCheckedLeafs]);

  // Get the filtered data using useMemo
  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  // Set initial radar data
  React.useEffect(() => {
    if (radarData) {
      // Add project-specific data to leaf nodes
      const enrichDataWithProjectData = (data) => {
        if (!data) return null;

        const enrichNode = (node) => {
          if (node.segmentType === SEGMENT_TYPE.LEAF) {
            // Find the project leaf segment for this node
            const projectLeafSegment = projectData.leafSegmentProjects?.find(
              segment => segment.leafSegment?.id === node.leafSegmentId
            );

            return {
              ...node,
              projectData: projectLeafSegment || null
            };
          }

          // If it's not a leaf node, process its subcategories
          if (node.subcategories?.length > 0) {
            return {
              ...node,
              subcategories: node.subcategories.map(enrichNode)
            };
          }

          return node;
        };

        return enrichNode(data);
      };

      // Inject custom segments into the tree under "Custom Technologies" category
      const injectCustomSegments = (treeData) => {
        // Find all custom segments (where leafSegment is null but customSegment exists)
        const customSegments = projectData?.leafSegmentProjects?.filter(
          s => s.leafSegment == null && s.customSegment != null
        ) || [];

        if (customSegments.length === 0) {
          return treeData;
        }

        // Create "Custom Technologies" category node (first-level INTERNAL node)
        const customTechNode = {
          id: 'custom-technologies',
          name: 'Custom Technologies',
          segmentType: SEGMENT_TYPE.INTERNAL,
          internalSegmentId: 'custom-technologies',
          color: '#9C27B0',  // Purple color for custom category
          subcategories: [],
          segmentProcesses: []
        };

        // Create leaf nodes for each custom segment, sorted by name
        const sortedCustomSegments = [...customSegments].sort((a, b) =>
          (a.customSegment.name || '').localeCompare(b.customSegment.name || '')
        );

        sortedCustomSegments.forEach((segment) => {
          const customLeafNode = {
            id: segment.customSegment.id,
            name: segment.customSegment.name,
            segmentType: SEGMENT_TYPE.LEAF,
            leafSegmentId: segment.customSegment.id,
            isCustomSegment: true,  // Flag to identify custom segments
            tags: [],
            approvalStage: APPROVAL_STAGES.HOLD,
            projects: [],
            subcategories: [],
            projectData: segment
          };

          customTechNode.subcategories.push(customLeafNode);
        });

        // Inject as first-level category in original tree
        return {
          ...treeData,
          subcategories: [...(treeData.subcategories || []), customTechNode]
        };
      };

      const enrichedData = enrichDataWithProjectData(radarData);
      const dataWithCustomSegments = injectCustomSegments(enrichedData);
      setProjectHierarchyData(dataWithCustomSegments);

      // Set initial checked leafs based on project's assigned segments (including custom segments)
      const radarLeafs = projectData.leafSegmentProjects
        ?.filter((leafSegment) => leafSegment.leafSegment != null)
        ?.map((leafSegment) => leafSegment.leafSegment.id) || [];

      const customLeafs = projectData.leafSegmentProjects
        ?.filter((s) => s.leafSegment == null && s.customSegment != null)
        ?.map((s) => s.customSegment.id) || [];

      const initialLeafs = new Set([...radarLeafs, ...customLeafs]);
      setInitialCheckedLeafs(initialLeafs);
    }
  }, [radarData, projectData]);

  // Hooks for chart functionality
  const {
    selectedSubcategory,
    handleSelection,
    handleBack,
  } = useChartData(projectHierarchyData || {}, TREE_CHART_MODES.TREE);

  const {
    searchTerm,
    searchResults,
    showResults,
    isGlobalSearch,
    handleSearch,
    handleResultSelect,
    setIsGlobalSearch
  } = useSearch(filteredData || {}, selectedSubcategory);

  const {
    tooltipData,
    tooltipPosition,
    hoveredItem,
    handleHover,
    handleMouseOut
  } = useTooltip();

  const {
    treeZoomFactor,
    radarZoomFactor,
    panPosition,
    isDragging,
    handleZoomIn,
    handleZoomOut,
    handleResetPosition,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    getZoomFactor,
  } = useZoomPan();

  // Render content based on different states
  const renderContent = () => {
    // Early return if no project data
    if (!projectData?.leafSegmentProjects?.length) {
      return (
        <Box sx={{ p: 3 }}>
          <EmptyState
            title="No Technologies Assigned to Project"
            description="Please assign technologies to the project first to view its technology radar."
          />
        </Box>
      );
    }

    if (radarLoading) {
      return <LoadingSpinner message="Loading technology radar data..." />;
    }

    if (radarError) {
      return (
        <EmptyState
          title="Error Loading Technology Radar"
          description="Failed to load technology radar data. Please try again later."
        />
      );
    }

    return (
      <ChartPanel>
        <div style={{ display: 'flex', gap: '1rem', height: '100%' }}>
          <div style={{ flex: 1, position: 'relative' }}>
            <TreeChart
              data={filteredData}
              selectedSubcategory={selectedSubcategory}
              projectData={projectData}
              hoveredItem={hoveredItem}
              onHover={handleHover}
              onMouseOut={handleMouseOut}
              onSelection={handleSelection}
              onBack={handleBack}
              zoomFactor={treeZoomFactor}
              configMode={TREE_CHART_MODES.PROJECT_READ_ONLY}
              expandedPath={expandedPath}
              setExpandedPath={setExpandedPath}
              isLeafAssigned={isLeafAssigned}
              initialCheckedLeafs={initialCheckedLeafs}
              isTreeExpanded={isTreeExpanded}
              onTreeExpansionToggle={setIsTreeExpanded}
            />
            <div style={{ position: 'absolute', top: '10px', right: '10px', display: 'flex', gap: '1rem' }}>
              <ZoomControls
                zoomFactor={treeZoomFactor}
                onZoomIn={(isMax) => handleZoomIn(TREE_CHART_MODES.TREE, isMax)}
                onZoomOut={() => handleZoomOut(TREE_CHART_MODES.TREE)}
                onReset={() => handleResetPosition(TREE_CHART_MODES.TREE)}
              />
            </div>
          </div>
        </div>
      </ChartPanel>
    );
  };

  return (
    <FormContainer>
      <FormSection>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          {projectData?.id && !readOnly && (
            <HeaderButton onClick={() => navigate(`/trend-radar/${projectData.id}`)}>
              <FaPlus /> Manage Project Technologies
            </HeaderButton>
          )}
        </Box>
        {renderContent()}
      </FormSection>
    </FormContainer>
  );
};

export default ProjectRadar; 